import { Heading, Text } from "@solomon-labs/design/components/ui";
import React, { useCallback, useMemo, useState } from "react";

import { List } from "../../../components/Data/List";
import { Section } from "../../../components/Section";
import { StatusBadge } from "../../../components/StatusBadge/StatusBadge";
import { Button } from "../../../components/ui/button";
import { FormActions } from "../../../components/ui/form-actions";
import { useSimCursor } from "../../sim-cursor/SimCursor";

import { framable, type FramableProps } from "./framable";
import {
    NB_B_CONFIG,
    NB_B_MINIMUM_THRESHOLD_VALUE,
    NB_B_NEW_USER_BONUS_AMOUNT,
    NB_B_NEW_USER_JOINED_WITHIN_DAYS,
    NB_B_NEW_USER_VALUE,
    NB_B_PROGRAM_NAME,
    NB_MINIMUM_THRESHOLD,
    NB_NEW_USER
} from "./neobankFixtures";
import { PolicyCreator, type PolicyCreatorDemo } from "./PolicyCreator";
import {
    initialValueFor,
    POLICY_CATALOG,
    serializeNewUserConfig
} from "./policyFixtures";
import { PolicyRow } from "./PolicyRow";
import {
    type ProgramConfigControl,
    type ProgramConfigDemo,
    ProgramConfiguration
} from "./ProgramConfiguration";
import type { ConfigurablePolicy, Policy, ProgramConfig } from "./types";
import {
    editNumberFieldBeats,
    type SimScriptStep,
    typedNumberBeats,
    useSimScript
} from "./useSimScript";

/**
 * Neobank scenario — **Sim B: "Deposit Campaign"**.
 *
 * A single, looping, cursor-driven walkthrough of the neobank configuring a
 * 90-day deposit-growth campaign in PMP, then activating it. Same shape as
 * {@link NeobankContinuousProgramSim}: fixture-driven, props-only Views under
 * one {@link useSimCursor}, auto-played by {@link useSimScript}.
 *
 *   Phase 1 — Configuration: cursor picks **Fixed Length, Fixed Budget**, sets
 *     **Program Period = 90 days**, **Program Budget = 250,000 USDv** (funded
 *     from the rewards wallet), **Distribution Schedule = 7 days**.
 *   Phase 2 — Policies: cursor builds **Minimum Threshold 250 USDv** (the
 *     minimum deposit), then a **New User** policy (opens the creator, picks
 *     "New User" from the type Select, enters the bonus amount + "joined within
 *     N days", adds it) — a purpose-built signup-bonus policy covering the
 *     one-time deposit reward on its own.
 *   Phase 3 — Activation: cursor clicks **Add Program**; then the ENTIRE sim
 *     (builder view + cursor) FADES OUT to opacity 0, holds faded for ~3s, and
 *     the loop reset fades it back in and replays from the empty draft. No read
 *     view, no Pending → Active flip, no jump to the Programs rail.
 *
 * No wallet / RPC / orgApiFetch — pure fixtures; reduced-motion collapses to
 * the fully-built draft end-state.
 *
 * NEOBANK-CASE MAPPING + GAPS (see neobankFixtures for the full table):
 *  - 90-day campaign → Program Period = 90 days.                    ✅ real
 *  - Fixed budget from rewards wallet → Program Budget 250,000.     ✅ real
 *      (Funding is DERIVED — the Config "Funding" field reads the External line
 *      for a Fixed Budget; no separate Funding section)
 *  - Minimum deposit 250 USDv → Minimum Threshold.                  ✅ real
 *  - One-time signup bonus → New User policy (bonus + joined-within). ✅ real UI
 *      (consume-once + join-date enforcement are backend traits — see GAPS)
 */

const FIXED_PROGRAM_FIXTURE: ProgramConfig = {
    status: "in_progress",
    period: "3m"
};

interface ConfigDraft {
    programType: "fixed" | "continuous";
    budgetMode: "fixed" | "open";
    apyMode: "earned" | "base";
    baseApy: number;
    userPct: number;
    periodDays: number;
    // `null` = CLEARED mid-edit (renders blank). Both demoed cfg fields open at
    // their real default and clear to null before the new value types in.
    settlementCycleLength: number | null;
    settlementCycleUnit: "days" | "weeks";
    budget: number | null;
}

const EMPTY_DRAFT: ConfigDraft = {
    // Opens on the fresh-draft DEFAULTS: Continuous · Variable (earned) ·
    // Period 90 · Distribution 14 · Budget 100k. This sim builds a Fixed
    // Length, Fixed Budget program, so its FIRST beat clicks Program Type →
    // "Fixed Length, Fixed Budget" (no longer the default). Switching to Fixed
    // Length reveals the Program Period input already reading 90 (== the
    // campaign length == the default), so Period is NOT re-typed. The two
    // visible field EDITS are the non-default Budget (opens at 100k, edited to
    // 250k) and Distribution (opens at 14, edited to 7).
    programType: "continuous",
    budgetMode: "open",
    apyMode: "earned",
    baseApy: 0,
    // Default User % (not shown on the Fixed-Budget path this sim builds).
    userPct: 100,
    // Default period — revealed already reading 90 when Fixed Length is picked,
    // so it isn't typed (typing 90 would be an invisible no-op).
    periodDays: 90,
    // Demoed — OPENS at the real DEFAULT (14), then edited to the non-default
    // 7-day cadence (focus → clear → 7).
    settlementCycleLength: 14,
    settlementCycleUnit: "days",
    // Demoed — OPENS at the real DEFAULT (100000), then edited UP to the
    // non-default 250k budget (focus → clear → 2 → 25 → … → 250000).
    budget: 100000
};

interface AttachedPolicy {
    policy: Policy;
    value: number | string;
}

interface PoliciesFixture {
    attached: AttachedPolicy[];
    editorType: ConfigurablePolicy | null;
    editorValue: number | string;
}

const EMPTY_POLICIES: PoliciesFixture = {
    attached: [],
    editorType: null,
    editorValue: ""
};

const CFG_DEMO_IDLE: ProgramConfigDemo = { control: null, phase: "hover" };
const POL_DEMO_IDLE: PolicyCreatorDemo = { control: null, phase: "hover" };

const TRAVEL_MS = 560;
const HOVER_HOLD_MS = 440;
const PRESS_HOLD_MS = 360;
const OPTION_STEP_MS = 200;
/** Duration of the opacity fade-out after Add Program (and the fade-in on
 *  replay). Matches the `duration-500` Tailwind utility on the root wrapper. */
const FADE_MS = 500;
/** Hold the sim FADED OUT (opacity 0) before the loop reset fades it back in
 *  and replays — the requested ~3s dwell. */
const FADE_HOLD_MS = 3000;

export interface NeobankDepositCampaignProgramSimProps extends FramableProps {
    /** Pause (ms) after the faded-out hold before replaying. Defaults to 0 (the
     *  ~3s dwell lives in the faded-out hold beat). `-1` = play once and stop. */
    loopPauseMs?: number;
}

function NeobankDepositCampaignProgramSimView({
    loopPauseMs = 0
}: NeobankDepositCampaignProgramSimProps = {}): React.ReactElement {
    // `faded` drives the root wrapper's opacity: false = opacity 1 (visible),
    // true = opacity 0 (faded out after Add Program). Reset flips it back to
    // false on replay → CSS transition fades the rebuilt sim back in.
    const [faded, setFaded] = useState(false);
    const [draft, setDraft] = useState<ConfigDraft>(EMPTY_DRAFT);
    const [cfgDemo, setCfgDemo] = useState<ProgramConfigDemo>(CFG_DEMO_IDLE);
    const [policies, setPolicies] = useState<PoliciesFixture>(EMPTY_POLICIES);
    const [createHover, setCreateHover] = useState<"hover" | "press" | null>(
        null
    );
    const [polDemo, setPolDemo] = useState<PolicyCreatorDemo>(POL_DEMO_IDLE);
    const [ctaDemo, setCtaDemo] = useState<"hover" | "press" | null>(null);

    const cursor = useSimCursor();
    const {
        moveTo: moveCursor,
        click: clickCursor,
        hide: hideCursor,
        containerRef
    } = cursor;

    const patchDraft = useCallback((next: Partial<ConfigDraft>): void => {
        setDraft((d) => ({ ...d, ...next }));
    }, []);
    const patchPolicies = useCallback(
        (next: Partial<PoliciesFixture>): void => {
            setPolicies((p) => ({ ...p, ...next }));
        },
        []
    );

    const reset = useCallback((): void => {
        setFaded(false);
        setDraft(EMPTY_DRAFT);
        setCfgDemo(CFG_DEMO_IDLE);
        setPolicies(EMPTY_POLICIES);
        setCreateHover(null);
        setPolDemo(POL_DEMO_IDLE);
        setCtaDemo(null);
        hideCursor();
    }, [hideCursor]);

    const openEditor = useCallback(
        (policy: ConfigurablePolicy, value: number | string): void => {
            setPolicies((p) => ({
                ...p,
                editorType: policy,
                editorValue: value
            }));
        },
        []
    );
    const commitEditor = useCallback(
        (policy: ConfigurablePolicy, value: number | string): void => {
            setPolicies((p) => ({
                attached: [...p.attached, { policy, value }],
                editorType: null,
                editorValue: ""
            }));
        },
        []
    );

    // A cfg TOGGLE control (Program Type / Budget Type): travel → hover →
    // click, landing the selection. Used to click "Fixed Length, Fixed Budget"
    // (Continuous is now the default, so this program type must be selected).
    const cfgControlBeats = useCallback(
        (
            control: ProgramConfigControl,
            cursorKey: string,
            value: Partial<ConfigDraft>
        ): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: `cfg:${control}:move`,
                run: () => moveCursor(cursorKey, { durationMs: TRAVEL_MS })
            },
            {
                after: TRAVEL_MS,
                label: `cfg:${control}:hover`,
                run: () => setCfgDemo({ control, phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: `cfg:${control}:press`,
                run: (): void => {
                    clickCursor();
                    setCfgDemo({ control, phase: "press" });
                    patchDraft(value);
                }
            }
        ],
        [moveCursor, clickCursor, patchDraft]
    );

    // A numeric cfg FIELD the cursor EDITS a PRE-FILLED value on (Program
    // Budget, Distribution Schedule length): the field OPENS reading its real
    // default, then travel → hover → click-to-focus → CLEAR (blank) → the new
    // value TYPES in digit-by-digit into `key`. Thin wrapper over the shared
    // `editNumberFieldBeats` so all three program-builder sims share the one
    // "seed the default, then demo the edit" helper.
    const editCfgBeats = useCallback(
        (
            control: ProgramConfigControl,
            cursorKey: string,
            key: keyof ConfigDraft,
            target: number
        ): SimScriptStep[] =>
            editNumberFieldBeats({
                control,
                cursorKey,
                target,
                travelMs: TRAVEL_MS,
                hoverHoldMs: HOVER_HOLD_MS,
                typeStartAfter: 220,
                moveCursor,
                clickCursor,
                setDemo: (c, phase) => setCfgDemo({ control: c, phase }),
                clearValue: () =>
                    patchDraft({ [key]: null } as Partial<ConfigDraft>),
                setValue: (value) =>
                    patchDraft({ [key]: value } as Partial<ConfigDraft>)
            }),
        [moveCursor, clickCursor, patchDraft]
    );

    const createPolicyBeats = useCallback(
        (): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: "pol-create:move",
                run: (): void => {
                    setCfgDemo(CFG_DEMO_IDLE);
                    moveCursor("pol-create-policy", { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: "pol-create:hover",
                run: () => setCreateHover("hover")
            },
            {
                after: HOVER_HOLD_MS,
                label: "pol-create:press",
                run: (): void => {
                    clickCursor();
                    setCreateHover("press");
                }
            }
        ],
        [moveCursor, clickCursor]
    );

    // A value FIELD inside the editor that TYPES its number in (Minimum
    // Threshold amount): travel → hover → click-to-focus (seed 0), then the
    // value builds digit-by-digit. `setValue` maps each typed prefix onto the
    // editor value.
    const typedEditorBeats = useCallback(
        (
            cursorKey: string,
            seedZero: () => void,
            target: number,
            setValue: (value: number) => void
        ): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: `pol-editor:${cursorKey}:move`,
                run: (): void => {
                    setCreateHover(null);
                    moveCursor(cursorKey, { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: `pol-editor:${cursorKey}:hover`,
                run: () =>
                    setPolDemo({ control: "config-value", phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: `pol-editor:${cursorKey}:focus`,
                run: (): void => {
                    clickCursor();
                    setPolDemo({ control: "config-value", phase: "press" });
                    seedZero();
                }
            },
            ...typedNumberBeats({
                target,
                startAfter: 220,
                label: `pol-editor:${cursorKey}`,
                setValue
            })
        ],
        [moveCursor, clickCursor]
    );

    const addPolicyBeats = useCallback(
        (run: () => void): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: "pol-add:move",
                run: (): void => {
                    setCreateHover(null);
                    moveCursor("pol-add-policy", { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: "pol-add:hover",
                run: () => setPolDemo({ control: "add-policy", phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: "pol-add:press",
                run: (): void => {
                    clickCursor();
                    setPolDemo({ control: "add-policy", phase: "press" });
                    run();
                }
            }
        ],
        [moveCursor, clickCursor]
    );

    const typeSelectBeats = useCallback(
        (target: ConfigurablePolicy): SimScriptStep[] => {
            const container = (): HTMLElement | null => containerRef.current;
            const targetIndex = POLICY_CATALOG.findIndex(
                (p) => p.id === target.id
            );
            const walk = POLICY_CATALOG.slice(0, targetIndex + 1);
            return [
                {
                    after: TRAVEL_MS,
                    label: "pol-type:move",
                    run: (): void => {
                        setCreateHover(null);
                        moveCursor("pol-type-select", {
                            durationMs: TRAVEL_MS
                        });
                    }
                },
                {
                    after: TRAVEL_MS,
                    label: "pol-type:hover",
                    run: () =>
                        setPolDemo({ control: "type-select", phase: "hover" })
                },
                {
                    after: HOVER_HOLD_MS,
                    label: "pol-type:open",
                    run: (): void => {
                        clickCursor();
                        setPolDemo({
                            control: "type-select",
                            phase: "press",
                            open: true,
                            portalContainer: container()
                        });
                    }
                },
                ...walk.flatMap((p, i): SimScriptStep[] => {
                    const optKey = `pol-type-opt-${p.id}`;
                    const travel =
                        i === 0 ? HOVER_HOLD_MS + 60 : OPTION_STEP_MS;
                    const stepMs = i === 0 ? TRAVEL_MS : OPTION_STEP_MS;
                    return [
                        {
                            after: travel,
                            label: `pol-type:travel-${p.id}`,
                            run: () =>
                                moveCursor(optKey, { durationMs: stepMs })
                        },
                        {
                            after: i === 0 ? TRAVEL_MS : OPTION_STEP_MS,
                            label: `pol-type:highlight-${p.id}`,
                            run: () =>
                                setPolDemo({
                                    control: "type-select",
                                    phase: "press",
                                    open: true,
                                    portalContainer: container(),
                                    highlightType: p.id
                                })
                        }
                    ];
                }),
                {
                    after: HOVER_HOLD_MS,
                    label: "pol-type:select",
                    run: (): void => {
                        clickCursor();
                        patchPolicies({
                            editorType: target,
                            editorValue: initialValueFor(
                                POLICY_CATALOG,
                                target.id
                            )
                        });
                        setPolDemo({
                            control: "type-select",
                            phase: "press",
                            open: false
                        });
                    }
                }
            ];
        },
        [moveCursor, clickCursor, containerRef, patchPolicies]
    );

    const steps = useMemo<readonly SimScriptStep[]>(
        () => [
            // ── Phase 1 — Configuration (Fixed Length, Fixed Budget) ─────
            // Continuous is now the DEFAULT Program Type, so this Fixed Length,
            // Fixed Budget program must first CLICK Program Type → "Fixed
            // Length, Fixed Budget" (fixed program type + fixed budget). The
            // Period row then reveals the days input already reading 90 (== the
            // campaign length == the default), and the Program Budget field
            // appears. Period is NOT typed — 90 is already the revealed value,
            // so typing it would be an invisible no-op.
            ...cfgControlBeats("program-type", "cfg-program-type", {
                programType: "fixed",
                budgetMode: "fixed"
            }),
            // Program Budget — OPENS at the real default (100k), edited UP to
            // the non-default 250,000 USDv (focus → clear → 2 → 25 → … →
            // 250000).
            ...editCfgBeats(
                "budget",
                "cfg-budget",
                "budget",
                NB_B_CONFIG.budget
            ),
            // Distribution Schedule — OPENS at the default 14, edited to the
            // non-default 7-day weekly cadence (focus → clear → 7). The unit
            // stays on its "days" default.
            ...editCfgBeats(
                "settlement",
                "cfg-settlement",
                "settlementCycleLength",
                NB_B_CONFIG.settlementCycleLength
            ),
            {
                after: 260,
                label: "cfg:settle",
                run: (): void => setCfgDemo(CFG_DEMO_IDLE)
            },

            // ── Phase 2 — Policies ───────────────────────────────────────
            // Policy 1 — Minimum Threshold 250 USDv (default type).
            ...createPolicyBeats(),
            {
                after: 260,
                label: "open-minimum-threshold",
                run: (): void => {
                    setCreateHover(null);
                    openEditor(
                        NB_MINIMUM_THRESHOLD,
                        initialValueFor(POLICY_CATALOG, NB_MINIMUM_THRESHOLD.id)
                    );
                }
            },
            // The Minimum Threshold field starts EMPTY; TYPE the 250 USDv
            // minimum deposit (2 → 25 → 250).
            ...typedEditorBeats(
                "pol-config-value",
                () => patchPolicies({ editorValue: "" }),
                NB_B_MINIMUM_THRESHOLD_VALUE,
                (value) => patchPolicies({ editorValue: value })
            ),
            ...addPolicyBeats(() =>
                commitEditor(NB_MINIMUM_THRESHOLD, NB_B_MINIMUM_THRESHOLD_VALUE)
            ),

            // Policy 2 — New User → the one-time signup bonus. Open the creator,
            // pick "New User" from the type Select → the editor is now on New
            // User with BOTH fields EMPTY. The cursor TYPES the bonus amount (25
            // USDv), then the "joined within" window (30 days), then Add Policy.
            ...createPolicyBeats(),
            {
                after: 260,
                label: "open-new-user",
                run: (): void => {
                    setCreateHover(null);
                    openEditor(
                        NB_MINIMUM_THRESHOLD,
                        initialValueFor(POLICY_CATALOG, NB_MINIMUM_THRESHOLD.id)
                    );
                }
            },
            ...typeSelectBeats(NB_NEW_USER),
            // Focus the Bonus amount field (starts empty) + TYPE 25 (2 → 25).
            // joined-within stays null (empty) while the bonus fills.
            ...typedEditorBeats(
                "pol-config-value",
                () =>
                    patchPolicies({
                        editorValue: serializeNewUserConfig({
                            bonusAmount: null,
                            joinedWithinDays: null
                        })
                    }),
                NB_B_NEW_USER_BONUS_AMOUNT,
                (value) =>
                    patchPolicies({
                        editorValue: serializeNewUserConfig({
                            bonusAmount: value,
                            joinedWithinDays: null
                        })
                    })
            ),
            // Then travel to + focus the "Joined within" field and TYPE its
            // window (3 → 30) — same visible treatment as the bonus (both fields
            // cursor-typed from empty). The bonus (25) is preserved as each
            // joined-within prefix serializes in.
            {
                after: TRAVEL_MS,
                label: "new-user-joined:move",
                run: (): void => {
                    setCreateHover(null);
                    moveCursor("pol-joined-within", { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: "new-user-joined:hover",
                run: (): void =>
                    setPolDemo({ control: "joined-within", phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: "new-user-joined:focus",
                run: (): void => {
                    clickCursor();
                    setPolDemo({ control: "joined-within", phase: "press" });
                    // Seed the field blank (bonus preserved) so it types in fresh.
                    patchPolicies({
                        editorValue: serializeNewUserConfig({
                            bonusAmount: NB_B_NEW_USER_BONUS_AMOUNT,
                            joinedWithinDays: null
                        })
                    });
                }
            },
            ...typedNumberBeats({
                target: NB_B_NEW_USER_JOINED_WITHIN_DAYS,
                startAfter: 220,
                label: "new-user-joined",
                setValue: (value) =>
                    patchPolicies({
                        editorValue: serializeNewUserConfig({
                            bonusAmount: NB_B_NEW_USER_BONUS_AMOUNT,
                            joinedWithinDays: value
                        })
                    })
            }),
            ...addPolicyBeats(() =>
                commitEditor(NB_NEW_USER, NB_B_NEW_USER_VALUE)
            ),
            {
                after: 260,
                label: "pol:settle",
                run: (): void => {
                    setPolDemo(POL_DEMO_IDLE);
                    setCreateHover(null);
                }
            },

            // ── Phase 3 — Activation ─────────────────────────────────────
            {
                after: TRAVEL_MS + 120,
                label: "activate:move",
                run: (): void =>
                    moveCursor("nb-b-add-program", { durationMs: TRAVEL_MS })
            },
            {
                after: TRAVEL_MS,
                label: "activate:hover",
                run: (): void => setCtaDemo("hover")
            },
            {
                after: HOVER_HOLD_MS,
                label: "activate:press",
                run: (): void => {
                    clickCursor();
                    setCtaDemo("press");
                }
            },
            // After the click the ENTIRE sim (builder + cursor) FADES OUT to
            // opacity 0, holds faded for ~3s, then the loop reset fades it back
            // in and replays from the empty draft.
            {
                after: PRESS_HOLD_MS,
                label: "activate:fade-out",
                run: (): void => {
                    setCtaDemo(null);
                    hideCursor();
                    setFaded(true);
                }
            },
            {
                after: FADE_MS + FADE_HOLD_MS,
                label: "activate:fade-hold",
                run: (): void => {}
            }
        ],
        [
            cfgControlBeats,
            editCfgBeats,
            createPolicyBeats,
            typedEditorBeats,
            addPolicyBeats,
            typeSelectBeats,
            openEditor,
            commitEditor,
            patchPolicies,
            moveCursor,
            clickCursor,
            hideCursor
        ]
    );

    useSimScript({ reset, steps, loopPauseMs });

    const { attached, editorType, editorValue } = policies;
    const editorOpen = editorType !== null;

    const policyAction = (
        <Button
            variant="sunken"
            size="sm"
            disabled={editorOpen}
            data-sim-cursor-key="pol-create-policy"
            forceHover={createHover === "hover"}
            forcePressed={createHover === "press"}
        >
            +Create Policy
        </Button>
    );

    return (
        // Fade wrapper — after Add Program `faded` flips true → opacity 0; the
        // loop reset flips it back to false → opacity 1. `transition-opacity
        // duration-500` (Tailwind defaults) animates both directions.
        <div
            ref={cursor.containerRef}
            className={`relative transition-opacity duration-500 ${
                faded ? "opacity-0" : "opacity-100"
            }`}
        >
            <section className="py-4">
                <div className="px-4 flex items-center gap-8">
                    <Heading
                        title={NB_B_PROGRAM_NAME}
                        titleVariant="h2"
                        tone="base"
                        titleClassName="text-3xl"
                    />
                    <StatusBadge
                        state="pending"
                        label="Draft"
                        labelClassName="text-warning-base"
                    />
                </div>
            </section>

            <ProgramConfiguration
                program={FIXED_PROGRAM_FIXTURE}
                demo={cfgDemo}
                programType={draft.programType}
                setProgramType={(programType) => patchDraft({ programType })}
                budgetMode={draft.budgetMode}
                setBudgetMode={(budgetMode) => patchDraft({ budgetMode })}
                apyMode={draft.apyMode}
                setApyMode={(apyMode) => patchDraft({ apyMode })}
                baseApy={draft.baseApy}
                setBaseApy={(baseApy) => patchDraft({ baseApy })}
                userPct={draft.userPct}
                setUserPct={(userPct) => patchDraft({ userPct })}
                periodDays={draft.periodDays}
                setPeriodDays={(periodDays) => patchDraft({ periodDays })}
                settlementCycleLength={draft.settlementCycleLength}
                setSettlementCycleLength={(settlementCycleLength) =>
                    patchDraft({ settlementCycleLength })
                }
                settlementCycleUnit={draft.settlementCycleUnit}
                setSettlementCycleUnit={(settlementCycleUnit) =>
                    patchDraft({ settlementCycleUnit })
                }
                budget={draft.budget}
                setBudget={(budget) => patchDraft({ budget })}
            />

            <Section
                heading="Policies"
                className="pt-8"
                headerClassName="px-4 mb-6"
                action={policyAction}
            >
                {editorOpen && editorType && (
                    <div className="px-4 mb-4">
                        <PolicyCreator
                            available={POLICY_CATALOG}
                            controlledPolicyId={editorType.id}
                            controlledValue={editorValue}
                            onControlledValueChange={(v) =>
                                patchPolicies({ editorValue: v })
                            }
                            demo={polDemo}
                            onAdd={() => {}}
                            onCancel={() => {}}
                        />
                    </div>
                )}
                {attached.length > 0 && (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted block mt-6 mb-1 px-4"
                    >
                        Added
                    </Text>
                )}
                <List>
                    {attached.map(({ policy, value }) => (
                        <PolicyRow
                            key={policy.id}
                            policy={policy}
                            value={value}
                            onRemove={() => {}}
                        />
                    ))}
                </List>
            </Section>

            <section className="mt-8 mb-10 px-4 flex items-center gap-3">
                <FormActions
                    size="lg"
                    primaryLabel="Add Program"
                    onPrimary={() => {}}
                    cancelLabel="Cancel"
                    onCancel={() => {}}
                    primaryCursorKey="nb-b-add-program"
                    primaryForceHover={ctaDemo === "hover"}
                    primaryForcePressed={ctaDemo === "press"}
                />
            </section>
            {cursor.layer}
        </div>
    );
}

/**
 * Neobank "Deposit Campaign" walkthrough. Add `framed` to wrap it in its own
 * fixed-height {@link SimFrame} (the block owns its max height); omit it to
 * render bare.
 */
export const NeobankDepositCampaignProgramSim = framable(
    NeobankDepositCampaignProgramSimView,
    "NeobankDepositCampaignProgramSim"
);
